import { ref, computed, watch, provide, inject, type Ref, onMounted, nextTick } from 'vue'
import type { FormState } from '@/types'
import { useQuestionnaireStore } from '@/store/questionnaireStore.ts'
import { getComponent } from '../components/questionaire/componentTypesV3'
import {
  useFieldValidation as useValidationService,
  type FieldValidation
} from '@/services/validation.service.ts'
import { QuestionnaireSubmissionsService, type QuestionnaireSection } from '@/types/generated'
import { toast } from '@/components/ui/toast'

const fieldValidationErrors = ref<Record<string, boolean>>({})

function patchFieldData(uuid: string, value: any, submission: any) {
  const fieldHasErrors = fieldValidationErrors.value[uuid] || false
  if (fieldHasErrors) {
    return false
  }

  if (value === undefined || value === null || value === '') {
    return false
  }

  let updatedSchema: any[] | undefined
  if (submission?.schema && Array.isArray(submission.schema)) {
    updatedSchema = submission.schema.map((page: any) => ({
      ...page,
      items: page.items.map((item: any) => {
        if (item.uuid === uuid) {
          return { ...item, value: value }
        }
        return item
      })
    }))
  }

  return updatedSchema
}

function updateFieldValidationError(fieldId: string, hasError: boolean) {
  fieldValidationErrors.value[fieldId] = hasError
}

async function saveFieldData(uuid: string, value: any, submission: any) {
  const updatedSchema = patchFieldData(uuid, value, submission)
  if (!updatedSchema) {
    return false
  }

  try {
    await QuestionnaireSubmissionsService.questionnaireSubmissionsPartialUpdate({
      uuid: submission.uuid || '',
      requestBody: {
        schema: updatedSchema,
        closed: false
      }
    })

    if (submission && submission.schema) {
      submission.schema = updatedSchema
    }

    return true
  } catch (error) {
    console.error('Error updating submission:', error)
    toast({
      title: 'Oh, oh!',
      description: 'Something went wrong. Please try again.',
      variant: 'destructive'
    })
    return false
  }
}

export function provideQuestionnaire() {
  provide('fieldValidationErrors', fieldValidationErrors)
  provide('patchFieldData', patchFieldData)
  provide('updateFieldValidationError', updateFieldValidationError)
  provide('saveFieldData', saveFieldData)

  return {
    fieldValidationErrors,
    patchFieldData,
    updateFieldValidationError,
    saveFieldData
  }
}

export function useQuestionnaire() {
  const fieldValidationErrors = inject<Ref<Record<string, boolean>>>(
    'fieldValidationErrors',
    ref({})
  )
  const patchFieldData = inject<Function>('patchFieldData', () => false)
  const updateFieldValidationError = inject<Function>('updateFieldValidationError', () => {})
  const saveFieldData = inject<Function>('saveFieldData', () => false)

  return {
    fieldValidationErrors,
    patchFieldData,
    updateFieldValidationError,
    saveFieldData
  }
}

export function useQuestionnaireForm(rawSchema: QuestionnaireSection[]) {
  const questionnaireStore = useQuestionnaireStore()

  questionnaireStore.initializeForm(rawSchema)

  const { fieldValidationErrors, updateFieldValidationError, saveFieldData } = useQuestionnaire()

  const fieldsWithComponents = computed(() => {
    return questionnaireStore.flatFields.map((field) => ({
      ...field,
      component: getComponent(field.type)
    }))
  })

  const formState = ref<FormState>({
    activeFieldId: questionnaireStore.activeFieldId,
    activeFieldIndex: questionnaireStore.activeFieldIndex,
    valid: true,
    next: true,
    formLength: questionnaireStore.formLength,
    isLastField: questionnaireStore.isLastField,
    isFirstField: computed(() => questionnaireStore.isFirstField),
    errorLength: 0
  })

  // Watch for store changes and update local state
  const updateFormState = () => {
    formState.value.activeFieldId = questionnaireStore.activeFieldId
    formState.value.activeFieldIndex = questionnaireStore.activeFieldIndex
    formState.value.formLength = questionnaireStore.formLength
    formState.value.isLastField = questionnaireStore.isLastField
    updateValidationState()
  }

  const updateValidationState = () => {
    const totalErrorCount = Object.values(fieldValidationErrors.value).filter(
      (hasError) => hasError
    ).length

    const currentFieldHasError =
      fieldValidationErrors.value[questionnaireStore.activeFieldId] || false

    const currentField = questionnaireStore.getFieldByUuid(questionnaireStore.activeFieldId)
    const currentFieldValue = questionnaireStore.formData[questionnaireStore.activeFieldId]

    const isRequiredFieldEmpty =
      currentField &&
      !currentField.optional &&
      (currentFieldValue === undefined ||
        currentFieldValue === null ||
        currentFieldValue === '' ||
        (Array.isArray(currentFieldValue) && currentFieldValue.length === 0))

    formState.value.valid = !currentFieldHasError && !isRequiredFieldEmpty
    formState.value.errorLength = totalErrorCount
  }

  watch(fieldValidationErrors, updateValidationState, { deep: true })
  watch(() => questionnaireStore.formData, updateValidationState, { deep: true })
  watch(() => questionnaireStore.activeFieldIndex, updateFormState)
  watch(() => questionnaireStore.formLength, updateFormState)
  watch(() => questionnaireStore.isLastField, updateFormState)

  // Initialize validation state for the initial field
  nextTick(() => {
    updateFormState()
  })

  function validateField(value: boolean) {
    formState.value.valid = value
  }

  function handleFieldValidationError(fieldId: string, hasError: boolean) {
    updateFieldValidationError(fieldId, hasError)
  }

  function onSubmit() {
    if (!formState.value.valid) {
      validateField(false)
    } else {
      validateField(true)
      questionnaireStore.goToNextField()
      updateFormState()
    }
  }

  function back() {
    questionnaireStore.goToPreviousField()
    updateFormState()
  }

  function next() {
    questionnaireStore.goToNextField()
    updateFormState()
  }

  function jumpToField(uuid: string) {
    questionnaireStore.jumpToField(uuid)
    updateFormState()
  }

  function setToLastField() {
    const lastIndex = questionnaireStore.formLength - 1
    if (lastIndex >= 0) {
      questionnaireStore.jumpToField(questionnaireStore.flatFields[lastIndex]?.uuid || '')
      updateFormState()
    }
  }

  function getFieldByUuid(uuid: string) {
    return questionnaireStore.getFieldByUuid(uuid)
  }

  function updateField(uuid: string, value: any) {
    questionnaireStore.updateFormData(uuid, value)
  }

  const getGlobalValidationState = () => {
    const hasAnyErrors = Object.values(fieldValidationErrors.value).some((hasError) => hasError)
    const totalErrorCount = Object.values(fieldValidationErrors.value).filter(
      (hasError) => hasError
    ).length
    const fieldsWithErrors = Object.keys(fieldValidationErrors.value).filter(
      (fieldId) => fieldValidationErrors.value[fieldId]
    )

    return {
      hasAnyErrors,
      totalErrorCount,
      fieldsWithErrors,
      allValidationErrors: fieldValidationErrors.value
    }
  }

  return {
    formState,
    fieldValidationErrors,
    validateField,
    handleFieldValidationError,
    saveFieldData,
    onSubmit,
    back,
    next,
    jumpToField,
    setToLastField,
    getFieldByUuid,
    updateField,
    getGlobalValidationState,
    fields: fieldsWithComponents
  }
}

export function useFieldValidation(props: any, emit: any) {
  const fieldValidation = computed<FieldValidation>(() => ({
    tag: props.field.tag,
    tag_relationship: props.field.tag_relationship,
    tag_number: props.field.tag_number,
    type: props.field.type,
    optional: props.field.optional,
    choices: props.field.choices
  }))

  const { validate } = useValidationService(fieldValidation.value)

  const validationError = ref('')
  const isInvalid = ref(false)

  function validateCurrentValue() {
    const result = validate(props.modelValue)
    validationError.value = result.errors[0] || ''
    isInvalid.value = !result.valid
    emit('validation-error', !result.valid)
  }

  watch(
    () => props.field,
    () => {
      validateCurrentValue()
    },
    { deep: true }
  )

  onMounted(() => {
    const isEmptyArray = Array.isArray(props.modelValue) && props.modelValue.length === 0
    if (
      props.modelValue !== undefined &&
      props.modelValue !== null &&
      props.modelValue !== '' &&
      !isEmptyArray
    ) {
      validateCurrentValue()
    }
  })

  function handleInputChange(value: any) {
    emit('update:modelValue', value)

    const result = validate(value)
    validationError.value = result.errors[0] || ''
    isInvalid.value = !result.valid
    emit('validation-error', !result.valid)
  }

  return {
    validationError,
    isInvalid,
    handleInputChange,
    validate
  }
}
